import type { CalendarDate } from '@internationalized/date';
import { getDayOfWeek, isEqualDay, isSameDay, isSameMonth, isToday } from '@internationalized/date';
import type { AriaCalendarCellProps } from '@react-aria/calendar';
import { useCalendarCell } from '@react-aria/calendar';
import { useFocusRing } from '@react-aria/focus';
import { useDateFormatter, useLocale } from '@react-aria/i18n';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';

interface CalendarCellProps extends AriaCalendarCellProps {
  state: CalendarState | RangeCalendarState;
  currentMonth: CalendarDate;
}

export const CalendarCell = ({ state, currentMonth, ...props }: CalendarCellProps) => {
  let ref = useRef<HTMLElement>(null);
  let { cellProps, buttonProps, isPressed } = useCalendarCell(
    {
      ...props,
      isDisabled: !isSameMonth(props.date, currentMonth)
    },
    state,
    ref
  );
  let { hoverProps, isHovered } = useHover({});
  let dateFormatter = useDateFormatter({
    day: 'numeric',
    timeZone: state.timeZone,
    calendar: currentMonth.calendar.identifier
  });
  let isSelected = state.isSelected(props.date);
  let highlightedRange = 'highlightedRange' in state && state.highlightedRange;
  let isSelectionStart = highlightedRange && isSameDay(props.date, highlightedRange.start);
  let isSelectionEnd = highlightedRange && isSameDay(props.date, highlightedRange.end);
  let { locale } = useLocale();
  let dayOfWeek = getDayOfWeek(props.date, locale);
  let isRangeStart = isSelected && (dayOfWeek === 0 || props.date.day === 1);
  let isRangeEnd =
    isSelected &&
    (dayOfWeek === 6 || props.date.day === currentMonth.calendar.getDaysInMonth(currentMonth));
  let { focusProps, isFocusVisible } = useFocusRing();

  let date = props.date;
  let lastDate = useRef(null);
  if (lastDate.current && isEqualDay(date, lastDate.current)) {
    date = lastDate.current;
  }

  lastDate.current = date as any;

  let nativeDate = useMemo(() => date.toDate(state.timeZone), [date, state.timeZone]);
  let formatted = useMemo(() => dateFormatter.format(nativeDate), [dateFormatter, nativeDate]);

  return (
    <td {...cellProps} className="" style={{ width: `${100 / 7}%` }}>
      <span
        {...mergeProps(buttonProps, hoverProps, focusProps)}
        ref={ref}
        className={classNames('flex justify-center py-3 text-sm', {
          'is-today-class rounded-full  bg-blue-100': isToday(props.date, state.timeZone),
          'is-selected-class rounded-full border-0 bg-blue-500 text-white': isSelected,
          'is-focused-class': state.isCellFocused(props.date) && isFocusVisible,
          'is-disabled-class': state.isCellDisabled(props.date),
          'is-outsideMonth-class text-gray-300': !isSameMonth(props.date, currentMonth),
          'is-range-start-class': isRangeStart,
          'is-range-end-class': isRangeEnd,
          'is-range-selection-class': isSelected && 'highlightedRange' in state,
          'is-selection-start-class': isSelectionStart,
          'is-selection-end-class': isSelectionEnd,
          'is-hovered-class rounded-full  hover:bg-blue-200': isHovered,
          'is-pressed-class': isPressed
        })}
      >
        <span className={`date-text-${props.date.day} text-center`}>{formatted}</span>
      </span>
    </td>
  );
};
